import React, { Component } from "react";
import { connect, ConnectedProps } from "react-redux";
import { SubscriptionIntervalEnum } from "onnit-ui/lib/@types/enums/SubscriptionInterval";
import { Favorite } from "onnit-ui/lib/@types/interfaces/favorites/Favorite";
import CartProductMutable from "./CartProductMutable";
import Cart from "../../../../interfaces/cart/Cart";
import { addProduct, removeProductFromFavorites, saveProductToFavorites, updateProduct } from "../../../../actions/cart/cartProductActions";
import { addAppMessage, clearAppMessages, setIsLoading } from "../../../../actions/appActions";
import ErrorMessageEnum from "../../../../enums/ErrorMessageEnum";
import PageNameEnum from "../../../../enums/PageNameEnum";
import CartProduct from "../../../../interfaces/cart/CartProduct";
import CartBrowserStorageService from "../../../../services/CartBrowserStorageService";
import SavedCartProduct from "./SavedCartProduct";

interface Props extends ReduxProps {
    cart: Cart;
}

interface SavedProduct {
    cartProduct: CartProduct;
    index: number;
    wasCreated: boolean;
    favorite: Favorite;
}

class CartProductListMutable extends Component<Props> {
    savedProduct: SavedProduct | null = null;

    private setError = (error: string) => {
        this.props.addAppMessage(error, [PageNameEnum.CART]);
    };

    private clearError = () => {
        this.props.clearAppMessages();
    };

    private async updateProduct(productId: number, quantity: number, interval: SubscriptionIntervalEnum | null, doClearSavedProduct = true): Promise<void> {
        if (doClearSavedProduct) {
            this.savedProduct = null;
        }
        try {
            this.clearError();
            this.props.setIsLoading(true);

            await this.props.updateProduct({
                product_id: productId,
                quantity,
                subscription_interval: interval,
                product_stock_priority_token: null,
            }, this.props.cart.cart_uuid);
        } finally {
            this.props.setIsLoading(false);
        }
    }

    // ------------------------- [ Event Handlers ] -------------------------

    private onRemove = (productId: number): void => {
        this.savedProduct = null;
        // Setting quantity to 0 removes the product.
        this.updateProduct(productId, 0, null).catch((error) => {
            console.error("Failed to remove cart product.", error);
            this.setError(error?.response?.data?.error_message ?? ErrorMessageEnum.GENERIC);
        });
    };

    private onChange = (productId: number, quantity: number, interval: SubscriptionIntervalEnum | null): void => {
        this.savedProduct = null;
        this.updateProduct(productId, quantity, interval).catch((error) => {
            console.error("Failed to update cart product.", error);
            this.setError(error?.response?.data?.error_message ?? ErrorMessageEnum.GENERIC);
        });
    };

    private onSave = async (cartProduct: CartProduct) => {
        const { customer_id, products } = this.props.cart;
        if (customer_id) {
            this.props.setIsLoading(true);
            try {
                // Save the product to favorites.
                const response = await saveProductToFavorites(customer_id, cartProduct.product.id);
                // Save product and the array index to persist message box once between renders.
                const cartProductIndex = products.findIndex((prod: CartProduct) => prod.product.id === cartProduct.product.id);
                this.savedProduct = { cartProduct, index: cartProductIndex, ...response };
                // Remove product from the cart.
                await this.updateProduct(cartProduct.product.id, 0, null, false);
                // Invalidate localStorage cache for when the user returns product pages.
                CartBrowserStorageService.removeFavorites();
            } catch (err) {
                console.log(err);
                this.setError("Sorry, we could not save this product for later.");
            } finally {
                this.props.setIsLoading(false);
            }
        }
    };

    private onUndoSave = async () => {
        if (this.savedProduct) {
            this.props.setIsLoading(true);

            const config = {
                product_id: this.savedProduct.cartProduct.product.id,
                quantity: this.savedProduct.cartProduct.quantity,
                subscription_interval: this.savedProduct.cartProduct.subscription_interval,
                product_stock_priority_token: null,
            };

            if (this.props.cart.customer_id && this.savedProduct.wasCreated) {
                try {
                    removeProductFromFavorites(this.props.cart.customer_id, this.savedProduct.favorite.customer_favorite_id);
                } catch (err) {
                    console.error("Failed to remove product from favorites when undoing save later.", err);
                }
            }

            // Add the product back
            this.savedProduct = null;
            try {
                await this.props.addProduct(config, this.props.cart.cart_uuid);
            } catch (err) {
                console.info("Failed to add product back to the cart when undoing save later.", err);
                this.setError("Sorry, we could not add this product back to your bag.");
            } finally {
                this.props.setIsLoading(false);
            }
        }
    };

    // ------------------------- [ Lifecycle Methods ] -------------------------

    render() {
        if (this.savedProduct) {
            // Merge in saved product for proper list order.
            this.props.cart.products.splice(this.savedProduct.index, 0, this.savedProduct.cartProduct);
        }

        return (
            <>
                {this.props.cart.products.map((cartProduct) => {
                    if (this.savedProduct?.cartProduct.product.id === cartProduct.product.id) {
                        return (
                            <SavedCartProduct
                                key={cartProduct.product.id}
                                cartProduct={this.savedProduct.cartProduct}
                                onUndoSave={this.onUndoSave}
                            />
                        );
                    }

                    return (
                        <CartProductMutable
                            key={cartProduct.product.id}
                            cart={this.props.cart}
                            cartProduct={cartProduct}
                            onQuantityChange={(newQuantity: number) => {
                                this.onChange(
                                    cartProduct.product.id,
                                    newQuantity,
                                    cartProduct.subscription_interval,
                                );
                            }}
                            onSubIntervalChange={(newInterval: SubscriptionIntervalEnum | null) => {
                                this.onChange(
                                    cartProduct.product.id,
                                    cartProduct.quantity,
                                    newInterval,
                                );
                            }}
                            onRemove={() => this.onRemove(cartProduct.product.id)}
                            onSave={() => this.onSave(cartProduct)}
                        />
                    );
                })}
            </>
        );
    }
}

const connector = connect(
    null,
    {
        setIsLoading,
        addProduct,
        updateProduct,
        addAppMessage,
        clearAppMessages,
        saveProductToFavorites,
        removeProductFromFavorites,
    },
);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(CartProductListMutable);
